const BLUR_RADIUS_RATIO = 0.02;

function getOvalRatio(canvasWidth: number) {
  return canvasWidth > 1000 ? 4.5 : 2.5;
}

type BlurPhotoProps = {
  photo: string;
};

type DrawCanvasProps = {
  ctx: CanvasRenderingContext2D;
  img: HTMLImageElement;
  canvasWidth: number;
  canvasHeight: number;
};

type ImageDataRGBAFn = (
  imageData: ImageData,
  top: number,
  left: number,
  width: number,
  height: number,
  radius: number,
) => void;

const applyBlurEffect = (
  {ctx, img, canvasWidth, canvasHeight}: DrawCanvasProps,
  imageDataRGBA: ImageDataRGBAFn,
) => {
  ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
  const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
  const blurRadius = Math.round(Math.min(canvasWidth, canvasHeight) * BLUR_RADIUS_RATIO);

  imageDataRGBA(imageData, 0, 0, canvasWidth, canvasHeight, blurRadius);
  ctx.putImageData(imageData, 0, 0);
};

const setCanvasElementOnDom = (width: number, height: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  document.body.appendChild(canvas);
  return canvas;
};

type CreateOvalShapeProps = Omit<DrawCanvasProps, 'img'>;
const createOvalShape = ({ctx, canvasWidth, canvasHeight}: CreateOvalShapeProps) => {
  ctx.save();
  ctx.beginPath();
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;
  const radiusX = canvasWidth / getOvalRatio(canvasWidth);
  const radiusY = canvasHeight / 2.5;
  ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
  ctx.clip();
  return {centerX, centerY, radiusX, radiusY};
};

const drawImageInOval = ({ctx, img, canvasWidth, canvasHeight}: DrawCanvasProps) => {
  ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
  ctx.restore();
};

type CreateDashBorderProps = Pick<DrawCanvasProps, 'ctx'> & {
  centerX: number;
  centerY: number;
  radiusX: number;
  radiusY: number;
};
const createDashBorder = ({
  ctx,
  centerX,
  centerY,
  radiusX,
  radiusY,
}: CreateDashBorderProps) => {
  ctx.beginPath();
  ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
  ctx.strokeStyle = 'rgb(50, 215, 190)';
  ctx.lineWidth = 5;
  ctx.setLineDash([10, 10]);
  ctx.stroke();
  ctx.setLineDash([]);
};

const blurSelfiePhoto = async ({photo}: BlurPhotoProps): Promise<string | undefined> => {
  if (photo) {
    const {imageDataRGBA} = await import('stackblur-canvas');
    const img = new Image();

    return new Promise((resolve, reject) => {
      img.onload = () => {
        const canvasWidth = img.naturalWidth;
        const canvasHeight = img.naturalHeight;
        let canvas: HTMLCanvasElement | null = null;
        let ctx: CanvasRenderingContext2D | null = null;

        try {
          canvas = setCanvasElementOnDom(canvasWidth, canvasHeight);
          ctx = canvas.getContext('2d');

          if (!ctx) {
            throw new Error('Canvas 2D context is not available');
          }

          applyBlurEffect({ctx, img, canvasWidth, canvasHeight}, imageDataRGBA);
          const {centerX, centerY, radiusX, radiusY} = createOvalShape({
            ctx,
            canvasWidth,
            canvasHeight,
          });
          drawImageInOval({ctx, img, canvasWidth, canvasHeight});
          createDashBorder({ctx, centerX, centerY, radiusX, radiusY});
          const dataUrl = canvas.toDataURL('image/jpeg');
          document.body.removeChild(canvas);
          resolve(dataUrl);
        } catch (error) {
          if (canvas && document.body.contains(canvas)) {
            document.body.removeChild(canvas);
          }
          reject(
            `Canvas processing failed: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          );
        }
      };

      img.onerror = () => {
        reject('Failed to load image for selfie processing');
      };

      img.src = photo;
    });
  }
  return undefined;
};

export {blurSelfiePhoto};
